/**
 * Dépenses et catégories.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";

export interface ExpenseCategory {
  id: string;
  name: string;
  color: string | null;
  isDefault: boolean;
}

export interface Expense {
  id: string;
  description: string;
  amountHt: number;
  tvaRate: number;
  amountTtc: number;
  expenseDate: string;
  paymentMethod: string | null;
  receiptUrl: string | null;
  /**
   * Le fournisseur, quand la dépense vient d'une facture d'achat classée.
   * Remonté jusqu'à l'écran pour une seule raison : rouvrir une dépense pour
   * en corriger le montant ne doit pas la détacher de son fournisseur.
   */
  supplierId: string | null;
  category: Pick<ExpenseCategory, "id" | "name" | "color"> | null;
  project: { id: string; name: string } | null;
}

/**
 * Montant TTC d'une dépense.
 *
 * Calculé, jamais saisi : deux champs indépendants finissent par se
 * contredire, et c'est le TTC qui sert au rapprochement bancaire.
 */
export function computeAmountTtc(amountHt: number, tvaRate: number): number {
  return Math.round(amountHt * (1 + tvaRate / 100) * 100) / 100;
}

/** Accepte « 1 234,56 » comme « 1234.56 » : les deux se saisissent en France. */
export function parseAmount(raw: string | number): number {
  if (typeof raw === "number") return raw;
  const cleaned = raw.replace(/\s/g, "").replace(",", ".");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
}

const amountField = z
  .union([z.string(), z.number()])
  .transform(parseAmount)
  .refine((n) => Number.isFinite(n) && n > 0, "Montant invalide");

export const saveExpenseInput = z.object({
  expenseId: z.string().uuid().optional(),
  confirmationId: z.string().optional(),

  description: z.string().trim().min(1, "La description est requise"),
  amountHt: amountField,
  tvaRate: z.coerce.number().min(0).max(100).default(20),
  expenseDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ"),
  paymentMethod: z.string().nullish(),
  categoryId: z
    .string()
    .uuid()
    .nullish()
    .or(z.literal("").transform(() => null)),
  projectId: z
    .string()
    .uuid()
    .nullish()
    .or(z.literal("").transform(() => null)),
  supplierId: z.string().uuid().nullish(),
  receiptUrl: z.string().nullish(),
});

/** Entrée validée, telle que la reçoit le service : montants numériques. */
export type SaveExpenseInput = z.infer<typeof saveExpenseInput>;

/**
 * Entrée brute, telle que la saisit un formulaire : le montant y est encore
 * la chaîne tapée par l'utilisateur, virgule comprise. C'est ce type que
 * `useForm` doit porter — l'autre décrit ce qui en sort après validation.
 */
export type SaveExpenseFormInput = z.input<typeof saveExpenseInput>;

export const listExpensesInput = z.object({
  confirmationId: z.string().optional(),
  /** Restreint au mois en cours. */
  currentMonthOnly: z.boolean().default(false),
  projectId: z.string().uuid().optional(),
});

export type ListExpensesInput = z.infer<typeof listExpensesInput>;

export const importExpensesInput = z.object({
  confirmationId: z.string().optional(),
  items: z
    .array(saveExpenseInput.omit({ expenseId: true, confirmationId: true }))
    .min(1, "Le fichier ne contient aucune ligne exploitable"),
});

export type ImportExpensesInput = z.infer<typeof importExpensesInput>;

export const createCategoryInput = z.object({
  confirmationId: z.string().optional(),
  name: z.string().trim().min(1, "Le nom de la catégorie est requis"),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "Couleur attendue au format #RRGGBB")
    .nullish(),
});

export type CreateCategoryInput = z.infer<typeof createCategoryInput>;

export interface SaveExpenseCommand {
  expenseId?: string;
  description: string;
  amountHt: number;
  tvaRate: number;
  amountTtc: number;
  expenseDate: string;
  paymentMethod: string | null;
  categoryId: string | null;
  projectId: string | null;
  supplierId: string | null;
  receiptUrl: string | null;
}

export interface ExpenseFilter {
  from?: string;
  projectId?: string;
}

export interface ExpenseRepository {
  list(ctx: ExecutionContext, filter?: ExpenseFilter): Promise<Expense[]>;
  save(ctx: ExecutionContext, command: SaveExpenseCommand): Promise<Expense>;
  saveMany(ctx: ExecutionContext, commands: SaveExpenseCommand[]): Promise<number>;
  listCategories(ctx: ExecutionContext): Promise<ExpenseCategory[]>;
  createCategory(
    ctx: ExecutionContext,
    name: string,
    color: string | null,
  ): Promise<ExpenseCategory>;
  ensureDefaultCategories(ctx: ExecutionContext): Promise<void>;
}

type ExpenseFields = Omit<SaveExpenseInput, "expenseId" | "confirmationId">;

function toCommand(input: ExpenseFields, expenseId?: string): SaveExpenseCommand {
  return {
    expenseId,
    description: input.description,
    amountHt: input.amountHt,
    tvaRate: input.tvaRate,
    amountTtc: computeAmountTtc(input.amountHt, input.tvaRate),
    expenseDate: input.expenseDate,
    paymentMethod: input.paymentMethod ?? null,
    categoryId: input.categoryId ?? null,
    projectId: input.projectId ?? null,
    supplierId: input.supplierId ?? null,
    receiptUrl: input.receiptUrl ?? null,
  };
}

/** Premier jour du mois courant, en date locale. */
function startOfMonth(today: Date): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
}

export function createExpenseServices(
  repo: ExpenseRepository,
  now: () => Date = () => new Date(),
) {
  const listerDepenses = defineService<ListExpensesInput, Expense[]>({
    name: "lister_depenses",
    level: ActionLevel.Read,
    handler: (ctx, input) =>
      repo.list(ctx, {
        from: input.currentMonthOnly ? startOfMonth(now()) : undefined,
        projectId: input.projectId,
      }),
  });

  const listerCategoriesDepense = defineService<
    { confirmationId?: string },
    ExpenseCategory[]
  >({
    name: "lister_categories_depense",
    level: ActionLevel.Read,
    handler: (ctx) => repo.listCategories(ctx),
  });

  const enregistrerDepense = defineService<SaveExpenseInput, Expense>({
    name: "enregistrer_depense",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) => repo.save(ctx, toCommand(input, input.expenseId)),
  });

  const importerDepenses = defineService<ImportExpensesInput, { imported: number }>({
    name: "importer_depenses",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => ({
      imported: await repo.saveMany(ctx, input.items.map((i) => toCommand(i))),
    }),
  });

  const creerCategorieDepense = defineService<CreateCategoryInput, ExpenseCategory>({
    name: "creer_categorie_depense",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) => repo.createCategory(ctx, input.name, input.color ?? null),
  });

  const initialiserCategoriesParDefaut = defineService<
    { confirmationId?: string },
    void
  >({
    name: "initialiser_categories_depense",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx) => repo.ensureDefaultCategories(ctx),
  });

  return {
    listerDepenses,
    listerCategoriesDepense,
    enregistrerDepense,
    importerDepenses,
    creerCategorieDepense,
    initialiserCategoriesParDefaut,
  };
}

export type ExpenseServices = ReturnType<typeof createExpenseServices>;
